// Flat, two-tone module illustrations for the landing page. Every scene is themed by
// two CSS variables set on the wrapper: --a (accent) and --a-soft (its pale tint), so
// one drawing works for any module colour in light and dark surfaces.

const INK = '#0a223d';
const PAPER = '#ffffff';
const MIST = '#eef2fa';
const LINE = '#d5dcec';

const Svg = ({ children, label }) => (
  <svg className="ill" viewBox="0 0 240 170" role={label ? 'img' : undefined} aria-label={label} aria-hidden={label ? undefined : true} focusable="false">
    <ellipse cx="120" cy="158" rx="92" ry="6" fill="rgba(10,34,61,0.08)" />
    {children}
  </svg>
);

const Lines = ({ x, y, widths, gap = 9, color = LINE }) =>
  widths.map((w, i) => <rect key={i} x={x} y={y + i * gap} width={w} height="4" rx="2" fill={color} />);

const SCENES = {
  fees: (
    <>
      <rect x="40" y="22" width="112" height="126" rx="10" fill={PAPER} stroke={LINE} strokeWidth="2" />
      <path d="M40 140l9 8 9-8 9 8 9-8 9 8 9-8 9 8 9-8 9 8 9-8v-8H40z" fill={MIST} />
      <rect x="54" y="36" width="42" height="8" rx="4" fill={INK} />
      <Lines x={54} y={56} widths={[84, 70, 78, 50]} />
      <rect x="54" y="104" width="84" height="22" rx="6" fill="var(--a-soft)" />
      <text x="62" y="120" fontSize="13" fontWeight="700" fill="var(--a)" fontFamily="Inter, sans-serif">Paid in full</text>
      <circle cx="176" cy="104" r="32" fill="var(--a)" />
      <circle cx="176" cy="104" r="24" fill="none" stroke="rgba(255,255,255,0.55)" strokeWidth="2" strokeDasharray="3 4" />
      <text x="176" y="115" textAnchor="middle" fontSize="30" fontWeight="700" fill={PAPER} fontFamily="Inter, sans-serif">{'₹'}</text>
      <ellipse cx="178" cy="142" rx="30" ry="7" fill="var(--a)" opacity=".25" />
      <circle cx="198" cy="52" r="15" fill="#10b981" />
      <path d="M191 52l5 5 9-10" fill="none" stroke={PAPER} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  attendance: (
    <>
      <rect x="32" y="26" width="176" height="122" rx="12" fill={PAPER} stroke={LINE} strokeWidth="2" />
      <path d="M32 38a12 12 0 0 1 12-12h152a12 12 0 0 1 12 12v14H32z" fill="var(--a)" />
      <rect x="62" y="16" width="6" height="18" rx="3" fill={INK} />
      <rect x="172" y="16" width="6" height="18" rx="3" fill={INK} />
      {Array.from({ length: 18 }).map((_, i) => {
        const col = i % 6, row = Math.floor(i / 6);
        const absent = i === 7, late = i === 13;
        return (
          <rect key={i} x={46 + col * 27} y={64 + row * 26} width="20" height="18" rx="5"
            fill={absent ? '#fecaca' : late ? '#fde68a' : 'var(--a-soft)'} />
        );
      })}
      {[0, 1, 2, 3, 4, 5, 8, 9, 10, 11, 12, 14, 15, 16].map((i) => {
        const col = i % 6, row = Math.floor(i / 6);
        return <path key={i} d={`M${51 + col * 27} ${74 + row * 26}l4 4 8-9`} fill="none" stroke="var(--a)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />;
      })}
      <circle cx="196" cy="128" r="26" fill={INK} />
      <text x="196" y="133" textAnchor="middle" fontSize="15" fontWeight="700" fill={PAPER} fontFamily="Inter, sans-serif">94%</text>
    </>
  ),
  exams: (
    <>
      <g transform="rotate(-6 100 90)">
        <rect x="42" y="24" width="112" height="128" rx="10" fill={PAPER} stroke={LINE} strokeWidth="2" />
        <rect x="56" y="38" width="50" height="8" rx="4" fill={INK} />
        <Lines x={56} y={58} widths={[84, 70, 80, 60, 76]} />
        <rect x="56" y="108" width="84" height="26" rx="6" fill="var(--a-soft)" />
        <path d="M64 124l6 6 12-14" fill="none" stroke="var(--a)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="92" y="119" width="40" height="5" rx="2.5" fill="var(--a)" opacity=".5" />
      </g>
      <circle cx="172" cy="62" r="32" fill="var(--a)" />
      <circle cx="172" cy="62" r="25" fill="none" stroke="rgba(255,255,255,.5)" strokeWidth="2" />
      <text x="172" y="72" textAnchor="middle" fontSize="28" fontWeight="800" fill={PAPER} fontFamily="Inter, sans-serif">A+</text>
      <g transform="rotate(38 190 128)">
        <rect x="150" y="122" width="76" height="12" rx="3" fill={INK} />
        <path d="M150 122l-14 6 14 6z" fill="#f3c9a0" />
        <rect x="214" y="122" width="12" height="12" rx="2" fill="#f87171" />
      </g>
    </>
  ),
  staff: (
    <>
      <path d="M120 62v22M60 104V84h120v20" fill="none" stroke={LINE} strokeWidth="3" strokeLinejoin="round" />
      <path d="M120 84v20" stroke={LINE} strokeWidth="3" />
      <circle cx="120" cy="38" r="24" fill="var(--a)" />
      <circle cx="120" cy="32" r="8" fill={PAPER} />
      <path d="M104 52a16 14 0 0 1 32 0z" fill={PAPER} />
      {[60, 120, 180].map((x, i) => (
        <g key={x}>
          <rect x={x - 28} y="104" width="56" height="46" rx="10" fill={PAPER} stroke={LINE} strokeWidth="2" />
          <circle cx={x} cy="120" r="9" fill={i === 1 ? 'var(--a)' : 'var(--a-soft)'} />
          <circle cx={x} cy="117" r="3.4" fill={i === 1 ? PAPER : 'var(--a)'} />
          <path d={`M${x - 6} 126a6 5 0 0 1 12 0z`} fill={i === 1 ? PAPER : 'var(--a)'} />
          <rect x={x - 17} y="135" width="34" height="4" rx="2" fill={LINE} />
          <rect x={x - 11} y="142" width="22" height="3.5" rx="1.75" fill={MIST} />
        </g>
      ))}
    </>
  ),
  enquiry: (
    <>
      <path d="M32 40a14 14 0 0 1 14-14h92a14 14 0 0 1 14 14v38a14 14 0 0 1-14 14H80l-22 18V92H46a14 14 0 0 1-14-14z" fill={PAPER} stroke={LINE} strokeWidth="2" />
      <Lines x={48} y={42} widths={[86, 60, 74]} />
      <path d="M112 84a14 14 0 0 1 14-14h72a14 14 0 0 1 14 14v30a14 14 0 0 1-14 14h-10v16l-20-16h-42a14 14 0 0 1-14-14z" fill="var(--a)" />
      <circle cx="136" cy="99" r="4" fill={PAPER} /><circle cx="152" cy="99" r="4" fill={PAPER} /><circle cx="168" cy="99" r="4" fill={PAPER} />
      <rect x="30" y="118" width="64" height="22" rx="11" fill="var(--a-soft)" />
      <circle cx="44" cy="129" r="5" fill="var(--a)" />
      <rect x="54" y="126" width="32" height="6" rx="3" fill="var(--a)" opacity=".55" />
      <circle cx="206" cy="40" r="16" fill="#10b981" />
      <path d="M199 40l5 5 9-10" fill="none" stroke={PAPER} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  inventory: (
    <>
      <rect x="22" y="58" width="196" height="8" rx="4" fill={INK} />
      <rect x="22" y="130" width="196" height="8" rx="4" fill={INK} />
      <rect x="34" y="28" width="50" height="30" rx="4" fill="var(--a)" />
      <rect x="34" y="28" width="50" height="9" rx="4" fill="rgba(255,255,255,.3)" />
      <rect x="92" y="14" width="40" height="44" rx="4" fill="var(--a-soft)" stroke="var(--a)" strokeWidth="2" />
      <rect x="140" y="34" width="62" height="24" rx="4" fill={PAPER} stroke={LINE} strokeWidth="2" />
      <Lines x={148} y={42} widths={[36, 24]} gap={8} />
      <rect x="34" y="96" width="38" height="34" rx="4" fill={PAPER} stroke={LINE} strokeWidth="2" />
      <rect x="80" y="84" width="56" height="46" rx="4" fill="var(--a)" />
      <path d="M80 100h56" stroke="rgba(255,255,255,.4)" strokeWidth="2" />
      <rect x="92" y="108" width="32" height="14" rx="3" fill={PAPER} />
      {[96, 100, 103, 107, 110, 114, 117, 121].map((x, i) => <rect key={x} x={x} y="111" width={i % 2 ? 1.5 : 2.5} height="8" fill={INK} />)}
      <rect x="146" y="92" width="58" height="38" rx="4" fill="var(--a-soft)" stroke="var(--a)" strokeWidth="2" />
      <circle cx="204" cy="86" r="14" fill="#f59e0b" />
      <text x="204" y="92" textAnchor="middle" fontSize="17" fontWeight="800" fill={PAPER} fontFamily="Inter, sans-serif">!</text>
    </>
  ),
  homework: (
    <>
      <rect x="38" y="20" width="118" height="132" rx="10" fill="var(--a)" />
      <rect x="48" y="20" width="108" height="132" rx="8" fill={PAPER} stroke={LINE} strokeWidth="2" />
      {[34, 54, 74, 94, 114, 134].map((y) => <rect key={y} x="33" y={y} width="16" height="7" rx="3.5" fill={INK} />)}
      <rect x="64" y="34" width="56" height="8" rx="4" fill={INK} />
      {[0, 1, 2].map((i) => (
        <g key={i}>
          <rect x="64" y={58 + i * 24} width="14" height="14" rx="4" fill={i < 2 ? 'var(--a)' : PAPER} stroke="var(--a)" strokeWidth="2" />
          {i < 2 && <path d={`M67 ${65 + i * 24}l3 3 5-6`} fill="none" stroke={PAPER} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />}
          <rect x="86" y={62 + i * 24} width={54 - i * 8} height="5" rx="2.5" fill={LINE} />
        </g>
      ))}
      <circle cx="190" cy="56" r="28" fill={PAPER} stroke="var(--a)" strokeWidth="5" />
      <path d="M190 40v17l11 7" fill="none" stroke={INK} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
      <g transform="rotate(32 188 126)">
        <rect x="156" y="120" width="64" height="12" rx="3" fill="#fbbf24" />
        <path d="M156 120l-13 6 13 6z" fill="#f3c9a0" /><path d="M143 126l-4 0" stroke={INK} strokeWidth="3" />
        <rect x="210" y="120" width="10" height="12" rx="2" fill="#f87171" />
      </g>
    </>
  ),
  notice: (
    <>
      <rect x="22" y="22" width="196" height="128" rx="12" fill="#e9d3b5" stroke="#c9a77d" strokeWidth="5" />
      <g transform="rotate(-5 66 70)"><rect x="38" y="38" width="62" height="64" rx="3" fill={PAPER} /><Lines x={46} y={52} widths={[46, 38, 42, 30]} /><circle cx="69" cy="38" r="5" fill="#ef4444" /></g>
      <g transform="rotate(4 128 62)"><rect x="104" y="34" width="56" height="52" rx="3" fill="var(--a)" /><Lines x={112} y={48} widths={[40, 32, 36]} color="rgba(255,255,255,.7)" /><circle cx="132" cy="34" r="5" fill="#2563eb" /></g>
      <g transform="rotate(-3 180 108)"><rect x="150" y="84" width="54" height="56" rx="3" fill="var(--a-soft)" /><Lines x={158} y={98} widths={[38, 30, 34]} color="var(--a)" /><circle cx="177" cy="84" r="5" fill="#10b981" /></g>
      <g transform="rotate(3 80 126)"><rect x="46" y="108" width="68" height="34" rx="3" fill="#fef3c7" /><Lines x={54} y={118} widths={[50, 38]} color="#d9a441" /><circle cx="80" cy="108" r="5" fill="#f59e0b" /></g>
    </>
  ),
  bell: (
    <>
      <path d="M120 24c-24 0-40 18-40 42v22c0 10-6 16-14 24h108c-8-8-14-14-14-24V66c0-24-16-42-40-42z" fill="var(--a)" />
      <path d="M96 38c-10 8-14 20-14 34" fill="none" stroke="rgba(255,255,255,.45)" strokeWidth="5" strokeLinecap="round" />
      <path d="M104 118a16 16 0 0 0 32 0z" fill={INK} />
      <rect x="116" y="14" width="8" height="12" rx="4" fill={INK} />
      <circle cx="170" cy="40" r="17" fill="#fbbf24" />
      <text x="170" y="46" textAnchor="middle" fontSize="17" fontWeight="800" fill={INK} fontFamily="Inter, sans-serif">3</text>
      <path d="M52 50c-8 10-12 20-12 32M188 82c0-12-4-22-12-32" fill="none" stroke="var(--a)" strokeWidth="4" strokeLinecap="round" opacity=".35" />
      <path d="M38 40c-12 14-18 28-18 44M202 84c0-16-6-30-18-44" fill="none" stroke="var(--a)" strokeWidth="4" strokeLinecap="round" opacity=".2" />
    </>
  ),
  website: (
    <>
      <rect x="26" y="26" width="188" height="122" rx="12" fill={PAPER} stroke={LINE} strokeWidth="2" />
      <path d="M26 38a12 12 0 0 1 12-12h164a12 12 0 0 1 12 12v12H26z" fill={INK} />
      <circle cx="42" cy="38" r="4" fill="#f87171" /><circle cx="56" cy="38" r="4" fill="#fbbf24" /><circle cx="70" cy="38" r="4" fill="#34d399" />
      <rect x="88" y="32" width="100" height="12" rx="6" fill="rgba(255,255,255,.18)" />
      <circle cx="82" cy="98" r="30" fill="var(--a)" />
      <ellipse cx="82" cy="98" rx="12" ry="30" fill="none" stroke="rgba(255,255,255,.6)" strokeWidth="2" />
      <path d="M52 98h60M57 82h50M57 114h50" stroke="rgba(255,255,255,.6)" strokeWidth="2" />
      <Lines x={130} y={72} widths={[64, 52, 58, 40]} />
      <rect x="130" y="112" width="52" height="18" rx="9" fill="var(--a)" />
    </>
  ),
  students: (
    <>
      <rect x="28" y="40" width="130" height="92" rx="12" fill={PAPER} stroke={LINE} strokeWidth="2" />
      <path d="M28 52a12 12 0 0 1 12-12h106a12 12 0 0 1 12 12v12H28z" fill="var(--a)" />
      <circle cx="62" cy="96" r="20" fill="var(--a-soft)" />
      <circle cx="62" cy="90" r="8" fill="var(--a)" />
      <path d="M46 112a16 12 0 0 1 32 0z" fill="var(--a)" />
      <Lines x={92} y={80} widths={[54, 44, 50]} gap={11} />
      <rect x="92" y="114" width="36" height="8" rx="4" fill="var(--a-soft)" />
      <path d="M170 34l40 16-40 16-40-16z" fill={INK} />
      <path d="M148 58v22c0 8 44 8 44 0V58l-22 9z" fill="#17375f" />
      <path d="M210 50v28" stroke="#fbbf24" strokeWidth="3" strokeLinecap="round" />
      <circle cx="210" cy="82" r="5" fill="#fbbf24" />
    </>
  ),
  phone: (
    <>
      <rect x="74" y="12" width="92" height="148" rx="16" fill={INK} />
      <rect x="80" y="20" width="80" height="132" rx="11" fill={PAPER} />
      <rect x="104" y="14" width="32" height="6" rx="3" fill={INK} />
      <rect x="88" y="30" width="64" height="26" rx="8" fill="var(--a)" />
      <circle cx="100" cy="43" r="6" fill="rgba(255,255,255,.6)" /><rect x="110" y="38" width="34" height="5" rx="2.5" fill={PAPER} /><rect x="110" y="46" width="22" height="4" rx="2" fill="rgba(255,255,255,.6)" />
      {[0, 1, 2].map((i) => (
        <g key={i}><rect x="88" y={64 + i * 28} width="64" height="22" rx="7" fill={i === 1 ? 'var(--a-soft)' : MIST} />
          <circle cx="99" cy={75 + i * 28} r="5" fill={i === 1 ? 'var(--a)' : LINE} />
          <rect x="109" y={70 + i * 28} width="34" height="4" rx="2" fill={LINE} /><rect x="109" y={77 + i * 28} width="22" height="3.5" rx="1.75" fill={LINE} opacity=".7" /></g>
      ))}
      <path d="M180 44c10 6 16 16 16 28M190 34c14 8 22 22 22 38" fill="none" stroke="var(--a)" strokeWidth="4" strokeLinecap="round" opacity=".4" />
      <path d="M60 120c-10-6-16-16-16-28M50 130c-14-8-22-22-22-38" fill="none" stroke="var(--a)" strokeWidth="4" strokeLinecap="round" opacity=".4" />
    </>
  ),
  signin: (
    <>
      <rect x="44" y="20" width="152" height="124" rx="14" fill={PAPER} stroke={LINE} strokeWidth="2" />
      <circle cx="120" cy="58" r="22" fill={MIST} />
      <g transform="translate(106 44) scale(0.58)">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </g>
      <rect x="64" y="92" width="112" height="24" rx="12" fill="var(--a)" />
      <rect x="86" y="101" width="68" height="6" rx="3" fill={PAPER} />
      <rect x="82" y="126" width="76" height="5" rx="2.5" fill={LINE} />
      <circle cx="198" cy="40" r="16" fill="#10b981" />
      <path d="M191 40l5 5 9-10" fill="none" stroke={PAPER} strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" />
    </>
  ),
  school: (
    <>
      <path d="M28 70L120 22l92 48z" fill="var(--a)" />
      <rect x="44" y="70" width="152" height="74" fill={PAPER} stroke={LINE} strokeWidth="2" />
      <rect x="108" y="100" width="24" height="44" rx="3" fill="var(--a)" />
      {[58, 78, 148, 168].map((x) => <rect key={x} x={x} y="88" width="14" height="22" rx="3" fill="var(--a-soft)" />)}
      <circle cx="120" cy="52" r="9" fill={PAPER} /><path d="M120 46v7l5 3" stroke="var(--a)" strokeWidth="2.4" fill="none" strokeLinecap="round" />
    </>
  ),
};

export default function Illustration({ name, accent = '#0a63d8', label }) {
  const scene = SCENES[name];
  if (!scene) return null;
  return (
    <span className="ill-wrap" style={{ '--a': accent, '--a-soft': `${accent}22` }}>
      <Svg label={label}>{scene}</Svg>
    </span>
  );
}
